/** Markdown renderer for public share views with GFM support and syntax highlighting */

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'

interface MarkdownRendererProps {
  content: string
  className?: string
}

/** Renders markdown content with GFM tables, code highlighting, and Tailwind prose styling */
export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  return (
    <div className={`prose prose-sm max-w-none md:prose-base
      prose-headings:font-display prose-headings:font-semibold prose-headings:text-fg
      prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-8
      prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-6
      prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-4
      prose-p:text-fg-muted prose-p:leading-relaxed
      prose-a:text-accent-text prose-a:no-underline hover:prose-a:underline
      prose-strong:text-fg
      prose-code:text-fg prose-code:bg-surface-muted prose-code:rounded prose-code:px-1.5 prose-code:py-0.5 prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
      prose-pre:bg-surface-muted prose-pre:text-fg prose-pre:border prose-pre:border-border prose-pre:rounded-lg
      prose-blockquote:border-border-strong prose-blockquote:text-fg-muted
      prose-li:text-fg-muted prose-li:marker:text-fg-subtle
      prose-img:rounded-lg prose-img:border prose-img:border-border
      prose-table:border-collapse
      prose-th:bg-surface-muted prose-th:text-fg prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:text-xs prose-th:font-medium prose-th:border prose-th:border-border
      prose-td:px-3 prose-td:py-2 prose-td:text-xs prose-td:text-fg-muted prose-td:border prose-td:border-border
      prose-hr:border-border
      ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          // Lazy load images with error fallback
          img: ({ src, alt, ...props }) => (
            <img
              src={src ?? ''}
              alt={alt || ''}
              loading="lazy"
              onError={(e) => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
              }}
              {...props}
            />
          ),
          // Open external links in new tab
          a: ({ href, children, ...props }) => {
            const isExternal = href?.startsWith('http')
            return (
              <a
                href={href}
                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                {...props}
              >
                {children}
              </a>
            )
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
